import { useState } from 'react'
import { authFetchJson, type ApiErrorWithDetails } from '../../lib/authFetch'
import { wizardTemplate } from '../../lib/templates/wizard'
import { getDomainConfig, DOMAIN_REGISTRY } from '../../lib/domainRegistry'
import type { Domain } from '../../lib/domainRegistry'
import { TemplateFormField } from './TemplateFormField'

interface AddProjectFormProps {
  domain: Domain
  onBack: () => void
  onSuccess: (projectId: number) => void
}

interface FormErrors {
  // Field-level errors from API validation
  fieldErrors?: Record<string, string[]>
  // Generic submit error
  submit?: string
}

export function AddProjectForm({
  domain,
  onBack,
  onSuccess,
}: AddProjectFormProps) {
  // Always use wizard template (Thin++ template for all domains)
  const template = wizardTemplate
  const domainConfig = getDomainConfig(domain)

  // Form state
  const [formData, setFormData] = useState<Record<string, any>>({
    domain,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<'form' | 'review'>('form')

  // Collect all fields from template
  const allFields = template.tabs.flatMap((tab) =>
    tab.sections.flatMap((section) => section.fields),
  )

  // Get subdomain options for the selected domain
  const getSubdomainOptions = () => {
    const domainReg = DOMAIN_REGISTRY[formData.domain as Domain]
    if (!domainReg?.subdomains || !Array.isArray(domainReg.subdomains)) {
      return []
    }
    return domainReg.subdomains.map((subdomain) => ({
      value: subdomain.id,
      label: subdomain.label,
    }))
  }

  const handleFieldChange = (key: string, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [key]: value }
      // If domain changed, clear subdomain
      if (key === 'domain') {
        updated.subdomain = null
      }
      return updated
    })
    // Clear field-level errors when user starts editing
    if (errors.fieldErrors?.[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        if (next.fieldErrors) {
          delete next.fieldErrors[key]
        }
        return next
      })
    }
  }

  const validateForm = (): boolean => {
    const fieldErrors: Record<string, string[]> = {}

    // Check required fields
    const requiredFields = allFields.filter(
      (f) => f.required || template.requiredForCreation.includes(f.key),
    )

    for (const field of requiredFields) {
      const value = formData[field.key]
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        fieldErrors[field.key] = [`${field.label} is required`]
      }
    }

    // Validate pattern if present
    for (const field of allFields) {
      const value = formData[field.key]
      if (
        value &&
        typeof value === 'string' &&
        field.validation?.pattern
      ) {
        const regex = new RegExp(field.validation.pattern)
        if (!regex.test(value)) {
          fieldErrors[field.key] = [`Invalid format for ${field.label}`]
        }
      }
    }

    setErrors({ fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined })
    return Object.keys(fieldErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    if (step === 'form') {
      setStep('review')
      return
    }

    setSubmitting(true)
    try {
      // Build payload from form data (only core fields for Thin++ wizard)
      const payload: Record<string, any> = {}

      for (const field of allFields) {
        const value = formData[field.key]
        // Only include non-empty, non-null values
        if (value !== null && value !== undefined && value !== '') {
          payload[field.key] = value
        }
      }

      if (import.meta.env.DEV) {
        console.log('[S9-08] Submitting Add Project wizard payload:', payload)
      }

      const response = await authFetchJson<{ id: number; title: string }>(
        '/api/v2/projects',
        {
          method: 'POST',
          json: payload,
        },
      )

      onSuccess(response.id)
    } catch (err) {
      // S9-08: Log full payload + response on error
      if (import.meta.env.DEV) {
        console.error('[S9-08] Add Project submission failed', {
          payload: formData,
          error: err instanceof Error ? err.message : String(err),
          response: err instanceof Error && 'response' in err ? (err as any).response : undefined,
        })
      }

      // Parse field-level errors from API response (S9-08)
      if (err instanceof Error && 'response' in err) {
        const apiError = (err as any).response as ApiErrorWithDetails | undefined
        if (apiError?.details?.fieldErrors) {
          setErrors({ fieldErrors: apiError.details.fieldErrors })
          // Keep form state (S9-10: Preserve form state on error)
          setStep('form')
        } else {
          setErrors({
            submit: apiError?.error || err.message || 'Failed to create project',
          })
          setStep('form')
        }
      } else {
        setErrors({
          submit: err instanceof Error ? err.message : 'Failed to create project',
        })
        setStep('form')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (step === 'review') {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">Review Project</h2>
          <p className="text-body text-text-secondary">
            Please review the information before submitting
          </p>
        </div>

        {/* Domain badge */}
        <div className="flex items-center gap-2 p-3 rounded-md bg-bg-surface-hover border border-border-subtle">
          <span className="text-2xl">{domainConfig?.icon}</span>
          <div className="flex-1">
            <p className="text-caption font-semibold text-text-secondary uppercase">Domain</p>
            <p className="text-body font-medium text-text-primary">
              {domainConfig?.label}
            </p>
          </div>
        </div>

        {/* Review fields */}
        <div className="space-y-4">
          {allFields.map((field) => {
            const value = formData[field.key]
            if (value === null || value === undefined || value === '') {
              return null
            }

            let displayValue = value
            if (field.type === 'enum' || field.type === 'multi_enum') {
              if (Array.isArray(value)) {
                displayValue = value
                  .map(
                    (v) =>
                      field.enumValues?.find((e) => e.value === v)
                        ?.label || v,
                  )
                  .join(', ')
              } else {
                displayValue =
                  field.enumValues?.find((e) => e.value === value)
                    ?.label || value
              }
            } else if (field.type === 'boolean') {
              displayValue = value ? 'Yes' : 'No'
            }

            return (
              <div key={field.key} className="flex flex-col gap-1">
                <p className="text-caption font-semibold text-text-secondary uppercase tracking-wide">
                  {field.label}
                </p>
                <p className="text-body text-text-primary">
                  {String(displayValue)}
                </p>
              </div>
            )
          })}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => setStep('form')}
            disabled={submitting}
            className="flex-1 px-4 py-2 rounded-md bg-bg-surface-hover text-text-secondary hover:text-text-primary border border-border-subtle transition-colors disabled:opacity-50"
          >
            Back to Edit
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-2 rounded-md bg-primary-red text-white hover:bg-primary-red/90 transition-colors disabled:opacity-50 font-medium"
          >
            {submitting ? 'Creating...' : 'Create Project'}
          </button>
        </div>

        {errors.submit && (
          <p className="text-caption text-primary-red text-center">
            {errors.submit}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{domainConfig?.icon}</span>
            <span className="text-caption font-semibold text-text-secondary uppercase tracking-wide">
              {domainConfig?.label}
            </span>
          </div>
          <h2 className="text-lg font-semibold text-text-primary">
            Add {domainConfig?.label} Project
          </h2>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="text-text-secondary hover:text-text-primary transition-colors p-1"
        >
          ✕
        </button>
      </div>

      {/* Form Content */}
      <div className="space-y-6">
        {template.tabs.map((tab) => (
          <div key={tab.id}>
            {tab.sections.map((section) => (
              <div key={section.id} className="flex flex-col gap-4">
                {/* Section Header */}
                <div>
                  <h3 className="text-body font-semibold text-text-primary">
                    {section.label}
                  </h3>
                  {section.description && (
                    <p className="text-body-sm text-text-secondary mt-1">
                      {section.description}
                    </p>
                  )}
                  <div className="h-px bg-border-subtle mt-3" />
                </div>

                {/* Section Fields */}
                <div className="grid grid-cols-2 gap-4">
                  {section.fields.map((field) => {
                    // S9-09: Hide subdomain if domain has no subdomains
                    if (field.key === 'subdomain') {
                      const subdomainOptions = getSubdomainOptions()
                      if (subdomainOptions.length === 0) {
                        return null
                      }
                      // Update enum values dynamically
                      field.enumValues = subdomainOptions
                    }

                    const width = field.width || 'full'
                    const colSpan =
                      width === 'full'
                        ? 'col-span-2'
                        : width === 'half'
                          ? 'col-span-1'
                          : 'col-span-1'

                    return (
                      <div key={field.key} className={colSpan}>
                        <TemplateFormField
                          field={field}
                          value={formData[field.key]}
                          onChange={handleFieldChange}
                          error={
                            errors.fieldErrors?.[field.key]
                              ? errors.fieldErrors[field.key].join('; ')
                              : undefined
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-4 border-t border-border-subtle">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 px-4 py-2 rounded-md bg-bg-surface-hover text-text-secondary hover:text-text-primary border border-border-subtle transition-colors"
        >
          Change Domain
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="flex-1 px-4 py-2 rounded-md bg-primary-red text-white hover:bg-primary-red/90 transition-colors disabled:opacity-50 font-medium"
        >
          {submitting ? 'Creating...' : 'Review & Submit'}
        </button>
      </div>

      {errors.submit && (
        <p className="text-caption text-primary-red text-center">
          {errors.submit}
        </p>
      )}
    </div>
  )
}
